import {
  CA_SURVIVAL_CARD_MIN_HEIGHT,
  CA_VENN_OUTER_MIN_W,
  CA_VENN_OUTER_MAX_H,
} from '../store/cohortAnalyzerLayoutConstants';

/** Shell, Venn chrome, forms, and chart tooltips (cohort analyzer layout). */
export const cohortAnalyzerCoreStyles = (theme) => ({
  container: {
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'row',
    flex: 1,
    overflow: 'hidden',
  },
  chartContainer: {
    backgroundColor: 'white',
    border: '1px solid #679AAA',
    borderRadius: '10px',
    boxShadow: 'none',
    flex: 1,
    display: 'flex',
    minHeight: 350,
    maxHeight: 620,
    minWidth: 500,
    flexDirection: 'column',
    overflow: 'hidden',
    marginLeft: 6,
    '& .App': {
      width: '95%',
      maxWidth: '95%',
      minWidth: '95%',
      height: '80%',
      padding: '24px 0px 36px 0px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',

      [theme.breakpoints.down('lg')]: {
        minWidth: '80%',
      },
    },
    '& .chart-container': {
      width: '100%',
      height: '100%',

      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      marginLeft: 0,
    },
    [theme.breakpoints.down('lg')]: {
      alignItems: 'center',
      marginLeft: 0,
    },
  },
  chartVennPlaceholder: {
    width: 725,
    [theme.breakpoints.down('lg')]: {
      width: '100%',
    },
  },
  /** Venn card shell — matches design reference (light panel + gray border). */
  vennDiagramCard: {
    backgroundColor: '#E8FBFE',
    border: '2px solid #ADADAD',
    borderRadius: 9,
    boxShadow: 'none',
    flex: '1 1 auto',
    alignSelf: 'stretch',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
    minWidth: CA_VENN_OUTER_MIN_W,
    maxHeight: CA_VENN_OUTER_MAX_H,
    minHeight: CA_SURVIVAL_CARD_MIN_HEIGHT,
    overflow: 'hidden',
    /** Fill card when resizing height; generic chartContainer uses 80% / 95% and hides growth. */
    '& .App': {
      width: '100%',
      maxWidth: '100%',
      minWidth: 0,
      height: '100%',
      flex: '1 1 auto',
      minHeight: 0,
      padding: '10px 6px 16px',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .chart-container': {
      width: '100%',
      height: '100%',
      minHeight: 0,
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: 0,
      overflow: 'hidden',
    },
  },
  vennToolbarRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '12px 14px 10px 8px',
    boxSizing: 'border-box',
    borderBottom: '0.5px solid #ADADAD',
  },
  vennToolbarLeading: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
    minWidth: 0,
  },
  vennToolbarTitle: {
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 600,
    fontSize: 18,
    lineHeight: 1.2,
    color: '#18677A',
    paddingLeft: 5,
    margin: 0,
    flexShrink: 0,
  },
  vennToolbarSpacer: {
    flex: 1,
    minWidth: 8,
  },
  vennToolbarTrailing: {
    alignSelf: 'center',
  },
  vennHeaderDivider: {
    height: 1,
    backgroundColor: '#ADADAD',
    margin: 0,
    flexShrink: 0,
  },
  vennSubHeader: {
    padding: '12px 16px 16px',
    flexShrink: 0,
    width: '100%',

  },
  vennSubHeaderPromptRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 2,
    flexWrap: 'wrap',
  },
  vennSubHeaderPrompt: {
    margin: 0,
    fontSize: 16,
    fontFamily: 'Poppins',
    color: '#5C5C5C',
    lineHeight: 1.35,
  },
  vennChartRadioContainer: {
    display: 'flex',
    flexDirection: 'row',
    /** One row of three options by default; wrap only when the viewport is narrow. */
    flexWrap: 'nowrap',
    marginTop: 10,
    gap: 16,
    padding: 0,
    marginLeft: 0,
    marginRight: 0,
    marginBottom: 0,
    color: '#1C2B33',
    fontSize: 15,
    fontFamily: 'Poppins, sans-serif',
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  leftAlignedText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    flex: 1,
    minHeight: 0,
    padding: '0 4px',
    fontSize: 12,
    lineHeight: 1.2,
    textAlign: 'center',
  },
  exploreButton: {
    boxSizing: 'border-box',
    minWidth: '189px',
    maxWidth: '189px',
    height: '41px',
    background: '#086C78',
    border: '1.25px solid #4EA1A1',
    borderRadius: '5px',
    color: 'white',
    fontWeight: '600',
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    justifyContent: 'center',
    alignItems: 'stretch',
    cursor: 'pointer',
    fontFamily: 'Poppins',
  },
  exploreButtonFaded: {
    boxSizing: 'border-box',
    fontFamily: 'Poppins',
    minWidth: '189px',
    maxWidth: '189px',
    height: '41px',
    background: '#BBC1C3',
    border: '1.25px solid #4EA1A1',
    borderRadius: '5px',
    color: 'white',
    fontWeight: '600',
    display: 'flex',
    flexDirection: 'row',
    gap: '6px',
    justifyContent: 'center',
    alignItems: 'stretch',
  },
  demoButton: {
    boxSizing: 'border-box',
    width: '197px',
    height: '29px',
    background: '#ECFAFC',
    border: '1.25px solid #375C67',
    borderRadius: '5px',
    color: '#375C67',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'pointer',
  },
  demoButtonFaded: {
    boxSizing: 'border-box',
    width: '197px',
    height: '29px',
    background: '#F5F5F5',
    border: '1.25px solid #CCCCCC',
    borderRadius: '5px',
    color: '#999999',
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '12px',
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    cursor: 'not-allowed',
  },
  demoButtonContainer: {
    position: 'relative',
    marginLeft: '20px',
  },
  demoTooltipContent: {
    fontFamily: 'Poppins',
    fontWeight: 400,
    fontSize: 13,
    margin: 0,
    textAlign: 'center',
    '& p': {
      margin: 0,
    },
  },
  chartTooltipContainer: {
    backgroundColor: 'white',
    padding: '10px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    boxShadow: 'none',
  },
  chartTooltipText: {
    margin: 0,
    fontFamily: 'Poppins',
    fontSize: '13px',
    fontWeight: 400,
    color: '#000000',
  },
  fieldsetReset: {
    all: 'unset',
    display: 'contents',
  },
  inputStyleContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginLeft: 15,
    '& img': {
      position: 'relative',
      right: 26,
      width: 14,
      height: 13,
    },
  },
  cardContent: {
    fontFamily: 'Nunito',
    fontSize: '12px',
    fontWeight: 300,
    lineHeight: '16.37px',
    textAlign: 'left',
  },
  inputStyle: {
    fontFamily: 'Nunito',
    fontWeight: 500,
    fontSize: '14px',
    lineHeight: 'normal',
    letterSpacing: 0,
    width: '349px',
    height: '26px',
    gap: '0px',
    borderRadius: '8px',
    margin: 'auto',
    marginLeft: '0px',
    paddingLeft: 13,
    border: '1px solid #8B98AF',
    textDecoration: 'none',
    '&::placeholder': {
      fontFamily: 'Nunito',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: 'normal',
      letterSpacing: 0,
      color: '#5D7B87 !important',
    },
  },
});
